import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { toolPreconditionService } from '@/services';
import type { PreconditionCombinator, ToolPreconditionInput } from '@/services';
import { invalidateQueries } from '@/lib/query-client';

const toolPreconditionsQueryAtom = (provider: string, tool: string) =>
  atomWithQuery(() => ({
    queryKey: ['tool-preconditions', provider, tool],
    queryFn: () => toolPreconditionService.list(provider, tool),
    enabled: !!provider && !!tool,
  }));

export function useToolPreconditions(provider: string, tool: string) {
  const atom = useMemo(() => toolPreconditionsQueryAtom(provider, tool), [provider, tool]);
  const [query] = useAtom(atom);
  return query;
}

const createPreconditionMutationAtom = atomWithMutation(() => ({
  mutationKey: ['tool-preconditions', 'create'],
  mutationFn: (data: ToolPreconditionInput) => toolPreconditionService.create(data),
  onSuccess: (_data, variables) => {
    invalidateQueries(['tool-preconditions', variables.provider, variables.tool]);
  },
}));

export function useCreatePrecondition() {
  const [mutation] = useAtom(createPreconditionMutationAtom);
  return mutation;
}

const updatePreconditionMutationAtom = atomWithMutation(() => ({
  mutationKey: ['tool-preconditions', 'update'],
  mutationFn: ({
    id,
    data,
  }: {
    id: string;
    provider: string;
    tool: string;
    data: Partial<ToolPreconditionInput> & { enabled?: boolean };
  }) => toolPreconditionService.update(id, data),
  onSuccess: (_data, variables) => {
    invalidateQueries(['tool-preconditions', variables.provider, variables.tool]);
  },
}));

export function useUpdatePrecondition() {
  const [mutation] = useAtom(updatePreconditionMutationAtom);
  return mutation;
}

const deletePreconditionMutationAtom = atomWithMutation(() => ({
  mutationKey: ['tool-preconditions', 'delete'],
  mutationFn: ({ id }: { id: string; provider: string; tool: string }) => toolPreconditionService.remove(id),
  onSuccess: (_data, variables) => {
    invalidateQueries(['tool-preconditions', variables.provider, variables.tool]);
  },
}));

export function useDeletePrecondition() {
  const [mutation] = useAtom(deletePreconditionMutationAtom);
  return mutation;
}

const setCombinatorMutationAtom = atomWithMutation(() => ({
  mutationKey: ['tool-preconditions', 'set-combinator'],
  mutationFn: ({ provider, tool, combinator }: { provider: string; tool: string; combinator: PreconditionCombinator }) =>
    toolPreconditionService.setCombinator(provider, tool, combinator),
  onSuccess: (_data, variables) => {
    invalidateQueries(['tool-preconditions', variables.provider, variables.tool]);
  },
}));

export function useSetPreconditionCombinator() {
  const [mutation] = useAtom(setCombinatorMutationAtom);
  return mutation;
}
